import { Features } from '~/components/FeatureToggle';
import { version } from '../version';

interface ConfigType {
    [key: string]: string | number | boolean | Record<string, unknown>;
}

/**
 * Represents an external document's URL and when the document in the URL was last updated.
 *
 * @property url - The URL as a string.
 * @property label - A human-readable label for the document, e.g. "Terms of Use".
 * @property lastUpdated - Date indicating the last time the URL was updated in ISO 8601 format, e.g. "2025-09-23".
 */
export type TrackedExternalDocument = {
    /** URL to document */
    url: string;
    /** A human-readable label for the document, e.g. "Terms of Use" */
    label: string;
    /** Date indicating the last time the URL was updated in ISO 8601 format, e.g. "2025-09-23" */
    lastUpdated: string;
};

class AppConfig {
    private static config: ConfigType | null = null;
    private static configPromise: Promise<ConfigType> | null = null;

    private static async loadConfig(): Promise<ConfigType> {
        if (this.configPromise) {
            return this.configPromise;
        }

        this.configPromise = (async () => {
            const response = await fetch('/app-config.json');
            if (!response.ok) {
                throw new Error(`Failed to load config: ${response.status}`);
            }
            const config = await response.json();
            this.config = config;
            return config;
        })();

        return this.configPromise;
    }

    public static get backendUrl(): string {
        return AppConfig.get('backendUrl', '');
    }

    /**
     * True when no backendUrl is configured, meaning the app runs
     * through the TPS connector (e.g. Eclipse) rather than a
     * standalone backend. Export pushes data back to the TPS;
     * otherwise the user downloads files to disk.
     */
    public static get isConnectorMode(): boolean {
        if (!this.config) {
            return false;
        }
        return !this.config['backendUrl'];
    }

    public static get dashboardUrl(): string {
        return AppConfig.get('dashboardUrl', 'https://dashboard.mvision.ai');
    }

    public static get configUrl(): string {
        return AppConfig.get('configUrl', '');
    }

    public static get portalUrl(): string {
        return AppConfig.get('portalUrl', 'https://portal.mvision.ai').replace(
            /\/+$/,
            ''
        );
    }

    /**
     * Retrieves the terms of use document configuration, or undefined if not configured.
     * This is mainly used in demo mode. Defaults to undefined to prevent linking to an obsolete
     * document by accident -- this field must be set in the deployment's app-config.json file
     * to display the document.
     *
     * @returns The `TrackedExternalDocument` representing the terms of use document,
     *          or `undefined` if the configuration is not set.
     */
    public static get termsOfUseDocument():
        | TrackedExternalDocument
        | undefined {
        return AppConfig.get('termsOfUseDocument', undefined);
    }

    public static get authority(): string {
        return AppConfig.get('authority', '');
    }

    public static get clientId(): string {
        return AppConfig.get('clientId', '');
    }

    /**
     * Issuer of the identity broker, empty when the application authenticates
     * against Azure Entra instead.
     */
    public static get oidcAuthority(): string {
        return AppConfig.get('oidcAuthority', '');
    }

    /** The application's client id at the identity broker. */
    public static get oidcClientId(): string {
        return AppConfig.get('oidcClientId', '');
    }

    public static get accessRightKey(): string {
        return AppConfig.get('accessRightKey', '');
    }

    public static get pollingIntervalMs(): number {
        return parseInt(AppConfig.get('pollingIntervalMs', '1000'));
    }

    public static get sentryDsn(): string {
        return AppConfig.get('sentryDsn', '');
    }

    public static get sentryTracesSampleRate(): number {
        return AppConfig.get('sentryTracesSampleRate', 1.0);
    }

    public static get sentryReplaysSessionSampleRate(): number {
        return AppConfig.get('sentryReplaysSessionSampleRate', 0.1);
    }

    public static get sentryReplaysOnErrorSampleRate(): number {
        return AppConfig.get('sentryReplaysOnErrorSampleRate', 1.0);
    }

    public static get clinicalUse(): boolean {
        return AppConfig.get('clinicalUse', false);
    }

    /** Default label to use when creating a new empty structure set. */
    public static get defaultNewStructureSetLabel(): string {
        return AppConfig.get('defaultNewStructureSetLabel', 'StructSet');
    }

    /** Manufacturer field used in DICOM files created within the application. */
    public static get dicomManufacturer(): string {
        return AppConfig.get('dicomManufacturer', 'MVision AI');
    }

    /**
     * Retrieves the implementation class UID from the application configuration.
     * If the value is not set in the configuration, a default unique implementation
     * class UID is returned.
     *
     * Note that during DICOM export this is most generally overwritten by dcmjs
     * with its own implementation class UID value.
     */
    public static get implementationClassUID(): string {
        return AppConfig.get(
            'implementationClassUID',
            '2.25.296926798722311868531127803130235741458'
        );
    }

    /** Manufacturer model name field used in DICOM files created within the application. */
    public static get dicomManufacturerModelName(): string {
        return AppConfig.get('dicomManufacturerModelName', 'Workspace+');
    }

    /** True if this app should run in demo mode, false otherwise. Defaults to false. */
    public static get isDemo(): boolean {
        return AppConfig.get('isDemo', false);
    }

    public static get version(): string {
        return version;
    }

    public static isFeatureEnabled(
        feature: Features,
        defaultTo = false
    ): boolean {
        const features = AppConfig.get('features', {});
        return (features as Record<string, boolean>)[feature] ?? defaultTo;
    }

    public static async initialize(): Promise<void> {
        await this.loadConfig();
    }

    public static get<T>(key: string, defaultTo: T): T {
        if (!this.config) {
            console.warn(
                'AppConfig not initialized, using default value for',
                key
            );
            return defaultTo;
        }
        return ((this.config as ConfigType)[key] as T) || defaultTo;
    }
}

export default AppConfig;
